/**
 * AutotilePass — Intelligent terrain transition resolution.
 *
 * Replaces the basic 4-bit TransitionPass with a TileMeta-aware system
 * that supports:
 *   - 4-bit cardinal autotiling (16 shapes)
 *   - 47-tile blob autotiling (diagonal-aware, 47 shapes)
 *   - Road network shapes (straights, curves, T-junctions, crossroads)
 *   - Random tile variants for visual variety
 *   - Multi-terrain transitions (grass→dirt→water with intermediate tiles)
 *
 * Requires a TileMeta instance with terrain groups defined.
 */

import { AUTOTILE_4BIT, AUTOTILE_47, AUTOTILE_ROAD, AUTOTILE_NONE } from '../TileMeta.js';

export class AutotilePass {
  /**
   * @param {import('../TileMeta.js').TileMeta} tileMeta
   */
  constructor(tileMeta) {
    this.meta = tileMeta;

    // Build reverse lookup: tileId → terrainGroupId
    // so we can identify terrain from the zone-resolved grid
    this._tileToTerrain = new Map();
    for (const terrainId of tileMeta.getTerrainIds()) {
      const tg = tileMeta.getTerrain(terrainId);
      // Map center tile and all variants back to this terrain
      if (tg.centerTile != null) this._tileToTerrain.set(tg.centerTile, terrainId);
      for (const v of Object.values(tg.variants || {})) {
        this._tileToTerrain.set(v, terrainId);
      }
      for (const v of (tg.randomVariants || [])) {
        this._tileToTerrain.set(v, terrainId);
      }
    }

  }

  /**
   * Process a tile grid, replacing tiles with correct autotile variants.
   *
   * @param {number[][]} grid — 2D tile grid (zone-resolved tile IDs)
   * @param {number} width
   * @param {number} height
   * @param {Function} [rngFn] — () => 0..1, for random variant selection
   * @returns {number[][]} — The mutated grid
   */
  process(grid, width, height, rngFn) {
    // Snapshot for consistent neighbor reads
    const snapshot = grid.map(row => [...row]);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const tileId = snapshot[y][x];
        const terrainId = this._tileToTerrain.get(tileId);
        if (!terrainId) continue;

        const tg = this.meta.getTerrain(terrainId);
        if (!tg || tg.autotileFormat === AUTOTILE_NONE) {
          // No autotiling — maybe apply random variant
          if (rngFn && tg) {
            grid[y][x] = this.meta.resolveVariant(terrainId, rngFn);
          }
          continue;
        }

        if (tg.autotileFormat === AUTOTILE_47) {
          grid[y][x] = this._resolve47(snapshot, x, y, terrainId, tg, width, height);
        } else if (tg.autotileFormat === AUTOTILE_ROAD) {
          grid[y][x] = this._resolveRoad(snapshot, x, y, terrainId, tg, width, height);
        } else {
          // Default: 4-bit cardinal
          grid[y][x] = this._resolve4bit(snapshot, x, y, terrainId, tg, width, height);
        }
      }
    }

    return grid;
  }

  /**
   * 4-bit cardinal autotiling.
   * Bitmask: N=1, E=2, S=4, W=8
   * Bit is SET when neighbor is DIFFERENT terrain (i.e., an edge exists).
   */
  _resolve4bit(snapshot, x, y, terrainId, tg, w, h) {
    let mask = 0;
    if (!this._isSameTerrain(snapshot, x, y - 1, terrainId, w, h)) mask |= 1;  // N
    if (!this._isSameTerrain(snapshot, x + 1, y, terrainId, w, h)) mask |= 2;  // E
    if (!this._isSameTerrain(snapshot, x, y + 1, terrainId, w, h)) mask |= 4;  // S
    if (!this._isSameTerrain(snapshot, x - 1, y, terrainId, w, h)) mask |= 8;  // W

    return this.meta.resolveAutotile(terrainId, mask);
  }

  /**
   * 47-tile blob autotiling.
   * Full 8-bit bitmask, then mask irrelevant diagonals, then lookup.
   *
   * Bit layout: NW=1, N=2, NE=4, W=8, E=16, SW=32, S=64, SE=128
   *
   * A diagonal bit only matters if BOTH adjacent cardinal neighbors are same terrain.
   * E.g., NW only matters if N and W are both same terrain.
   */
  _resolve47(snapshot, x, y, terrainId, tg, w, h) {
    const n  = this._isSameTerrain(snapshot, x, y - 1, terrainId, w, h);
    const e  = this._isSameTerrain(snapshot, x + 1, y, terrainId, w, h);
    const s  = this._isSameTerrain(snapshot, x, y + 1, terrainId, w, h);
    const ww = this._isSameTerrain(snapshot, x - 1, y, terrainId, w, h);

    let mask = 0;
    if (n)  mask |= 2;
    if (e)  mask |= 16;
    if (s)  mask |= 64;
    if (ww) mask |= 8;

    // Diagonals only matter when both adjacent cardinals match
    if (n && ww && this._isSameTerrain(snapshot, x - 1, y - 1, terrainId, w, h)) mask |= 1;   // NW
    if (n && e  && this._isSameTerrain(snapshot, x + 1, y - 1, terrainId, w, h)) mask |= 4;   // NE
    if (s && ww && this._isSameTerrain(snapshot, x - 1, y + 1, terrainId, w, h)) mask |= 32;  // SW
    if (s && e  && this._isSameTerrain(snapshot, x + 1, y + 1, terrainId, w, h)) mask |= 128; // SE

    return this.meta.resolveAutotile(terrainId, mask);
  }

  /**
   * Road network autotiling.
   * Uses 4-bit cardinal bitmask but interprets connections rather than edges.
   * Bit is SET when neighbor IS same terrain (opposite of terrain autotiling).
   */
  _resolveRoad(snapshot, x, y, terrainId, tg, w, h) {
    let mask = 0;
    if (this._isSameTerrain(snapshot, x, y - 1, terrainId, w, h)) mask |= 1;  // N
    if (this._isSameTerrain(snapshot, x + 1, y, terrainId, w, h)) mask |= 2;  // E
    if (this._isSameTerrain(snapshot, x, y + 1, terrainId, w, h)) mask |= 4;  // S
    if (this._isSameTerrain(snapshot, x - 1, y, terrainId, w, h)) mask |= 8;  // W

    return this.meta.resolveAutotile(terrainId, mask);
  }

  /**
   * Check if a neighbor cell belongs to the same terrain group.
   * Out-of-bounds counts as "same" (no edge at map border) to avoid
   * border artifacts. Override this behavior in subclass if needed.
   */
  _isSameTerrain(grid, x, y, terrainId, w, h) {
    if (x < 0 || y < 0 || x >= w || y >= h) return true; // OOB = same
    const neighborTile = grid[y][x];
    const neighborTerrain = this._tileToTerrain.get(neighborTile);

    // Same terrain group = same
    if (neighborTerrain === terrainId) return true;

    // Neighbor is a non-terrain tile (wall, floor, door, etc.) — treat as different
    if (neighborTerrain == null) return false;

    // Neighbor is a different terrain — check if it's an expected transition
    // transitionsTo terrains are treated as "different" (we WANT edge tiles here)
    // Unlisted terrains are also treated as "different"
    return false;
  }
}
